import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, movies } from '@/mocks/movies';
import { MovieCard } from '@/pages/home/components/MovieGrid';

const categoryNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  dongman: '动漫',
  zongyi: '综艺',
  jilupian: '纪录片',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const categoryName = categoryNameMap[activeCategory] || '电影';

  const filteredMovies = useMemo(() => {
    let result = movies.filter((m) => m.category === categoryName);
    if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result = [...result].sort((a, b) => b.year - a.year);
    }
    return result;
  }, [categoryName, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: categoryName },
          ]}
        />

        {/* Category Header */}
        <section className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-100 font-heading">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {filteredMovies.length} 部作品
              </p>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <div className="flex bg-background-100 rounded-full p-1 border border-background-200/40">
                {[
                  { value: 'default' as const, label: '默认' },
                  { value: 'rating' as const, label: '评分最高' },
                  { value: 'year' as const, label: '最新发布' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-4 py-1.5 rounded-full text-xs transition-all whitespace-nowrap cursor-pointer ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-white'
                        : 'text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/40 hover:border-primary-500/30'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Movie Grid */}
          {filteredMovies.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-500">
                <i className="ri-emotion-sad-line text-2xl"></i>
              </div>
              <p className="text-foreground-500">暂无内容，敬请期待</p>
            </div>
          )}
        </section>

        {/* SEO Structured Data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'CollectionPage',
              name: `大师兄影视 - ${categoryName}分类`,
              description: `大师兄影视${categoryName}分类，提供海量免费${categoryName}在线观看，高清画质不卡顿，全集播放。`,
              url: `https://www.dashixiong.com/category/${activeCategory}`,
              isPartOf: {
                '@type': 'WebSite',
                name: '大师兄影视',
                url: 'https://www.dashixiong.com/',
              },
            }),
          }}
        />
      </main>

      <Footer />
    </>
  );
}